import React, { useCallback, useEffect, useRef } from 'react';
import { TimePickerView, TimePickerViewProps } from './TimePicker.view';
import { InputErrorMessageSeverityType, SelectItem } from '../../forms/Select/Select.view';
import { useSelectedOrganization } from '../../../hooks/useSelectedOrganization';
import { TimeFormatSetting } from '../../../models/graphql';
import { UseFormReturn } from 'react-hook-form';

export type TimePickerControllerProps = {
  // extend your TimePickerViewProps
  actions?: TimePickerViewProps['actions'];
  ampmInputName: TimePickerViewProps['ampmInputName'];
  defaultDate?: Date;
  disabled?: TimePickerViewProps['disabled'];
  hoursInputName: TimePickerViewProps['hoursInputName'];
  label?: TimePickerViewProps['label'];
  minutesInputName: TimePickerViewProps['minutesInputName'];
  rules: TimePickerViewProps['rules'];
  size: TimePickerViewProps['size'];
  useFormInstance: TimePickerViewProps['useFormInstance'];
} & InputErrorMessageSeverityType;

export const TimePickerController = (props: TimePickerControllerProps): JSX.Element => {
  // Put All your methods and logic here
  const { defaultDate, useFormInstance, minutesInputName, hoursInputName, ampmInputName } = props;
  const formInstanceRef = useRef<UseFormReturn>(useFormInstance);
  const { selectedOrganization } = useSelectedOrganization();
  const timeFormat = selectedOrganization?.myUserAssignment?.settings.timeFormat ?? TimeFormatSetting.HOUR_12;
  const getMinutesOfHour = useCallback((): SelectItem[] => {
    const endCount = 60;
    const minutesList = [
      { label: '00', value: '00' },
      { label: '05', value: '05' }
    ];

    for (let i = 10; i < endCount; i += 5) {
      minutesList.push({ label: `${i}`, value: `${i}` });
    }

    return minutesList;
  }, []);
  const getHoursOfDay = useCallback(() => {
    const twelveHourMode = timeFormat === TimeFormatSetting.HOUR_12;
    const endCount = twelveHourMode ? 12 : 23;
    const hourList = [];

    if (!twelveHourMode) {
      hourList.push({ label: '00', value: '00' });
    }

    for (let i = 1; i <= endCount; i++) {
      const value = i < 10 ? `0${i}` : i;
      hourList.push({ label: `${twelveHourMode ? i : value}`, value: `${value}` });
    }

    return hourList;
  }, [timeFormat]);

  useEffect(() => {
    if (defaultDate) {
      //
      const hours = defaultDate.getHours();
      const minutes = defaultDate.getMinutes();
      const minutesStr = minutes < 10 ? `0${minutes}` : `${minutes}`;
      const twelveHourMode = timeFormat === TimeFormatSetting.HOUR_12;
      const formattedHour = twelveHourMode && hours > 12 ? hours - 12 : twelveHourMode && hours === 0 ? 12 : hours;
      const hoursStr = formattedHour < 10 ? `0${formattedHour}` : `${formattedHour}`;

      formInstanceRef.current.setValue(minutesInputName, minutesStr);
      formInstanceRef.current.setValue(hoursInputName, hoursStr);

      if (twelveHourMode) {
        formInstanceRef.current.setValue(ampmInputName, hours > 11 ? 'pm' : 'am');
      }
    }
  }, [defaultDate, timeFormat, minutesInputName, hoursInputName, ampmInputName]);

  const minutesItems = getMinutesOfHour();
  const hoursItems = getHoursOfDay();

  const viewProps: TimePickerViewProps = { ...props, minutesItems, hoursItems, timeFormat };
  return <TimePickerView {...viewProps} />;
};
